import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../../lib/api'
import { useConfirm } from '../../lib/confirm'
import { useCapabilities } from '../../lib/capabilities'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { ListResponse, Load, PersonOption } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select, Skeleton, StatusBadge,
} from '../../components/ui'
import { CloseIcon, PlusIcon, RouteIcon } from '../../components/icons'
import { Wizard } from '../../components/Wizard'

/** A truck as the picker needs it - see GET /ops/dispatch/trucks. */
interface TruckOption {
  id?: string; plate?: string; name?: string
  driver_name?: string; org_id?: string; org_name?: string
}

/* Loads — one screen, one idea.
 *
 * "Dispatch" and "Load" were two names for the same thing: dispatching IS
 * creating a load record, and the two screens showed the same rows. They are
 * merged here under a single heading, with dispatching as the action rather
 * than a separate destination.
 *
 * Capture and reporting stay separated: someone whose job is to record loads
 * gets a confirmation and nothing else, because the ledger of everyone's
 * loads is reporting, not capture. Holders of a reports capability see it. */

const TABS = [
  { value: 'all', label: 'All' },
  { value: 'incoming', label: 'Incoming' },
  { value: 'outgoing', label: 'Outgoing' },
]

/** What the cooperative actually hauls. A fixed list beats free text: it
 *  keeps the production and levy reports addable instead of scattering
 *  "copper", "Copper" and "cu" across the ledger. */
const MINERALS = [
  { value: 'tailings', label: 'Tailings' },
  { value: 'total_copper', label: 'Total copper' },
  { value: 'sulphate', label: 'Sulphate' },
]

/** Where loading happens - the cooperative's own operating sites. */
const SITES = ['Chingola', 'Luanshya', 'Mufulira', 'Ndola']

/** Fallback list if the provinces endpoint cannot be reached (offline, say).
 *  The authoritative list is GET /locations/provinces, which reads the same
 *  static/zambian_locations.json the rest of the app has always used. */
const PROVINCES_FALLBACK = [
  'Central Province', 'Copperbelt Province', 'Eastern Province',
  'Luapula Province', 'Lusaka Province', 'Muchinga Province',
  'North-Western Province', 'Northern Province', 'Southern Province',
  'Western Province',
]

export default function Dispatch() {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const caps = useCapabilities()
  const [tab, setTab] = useState('all')
  const [showForm, setShowForm] = useState(false)
  const [recipientQuery, setRecipientQuery] = useState('')
  const [recipients, setRecipients] = useState<PersonOption[]>([])
  const [recipient, setRecipient] = useState<PersonOption | null>(null)
  const [sent, setSent] = useState(false)

  // The ledger is reporting. A pure capturer records a load and is told it
  // worked - they do not get everyone else's movements.
  const canViewReports = caps.has('*')
    || caps.has('reports.view.sensitive')
    || caps.has('reports.view.operations')

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['ops', 'dispatch', 'loads', tab],
    queryFn: () => api.get<ListResponse<Load>>(`/ops/dispatch/loads?tab=${tab}`),
    enabled: canViewReports,
  })

  // Every truck a dispatcher may load, for the picker below. Typing a plate
  // meant guessing spacing and case, and being told "not found" for a truck
  // that plainly exists.
  const trucksQuery = useQuery({
    queryKey: ['ops', 'dispatch', 'trucks'],
    queryFn: () => api.get<{ ok: boolean; items: TruckOption[] }>('/ops/dispatch/trucks'),
  })

  // A load can leave the Copperbelt for anywhere in the country, so the
  // destination is any province.
  const provincesQuery = useQuery({
    queryKey: ['locations', 'provinces'],
    queryFn: () => api.get<{ ok: boolean; provinces: string[] }>('/locations/provinces'),
    staleTime: 24 * 60 * 60_000,
  })
  const provinces = provincesQuery.data?.provinces?.length
    ? provincesQuery.data.provinces
    : PROVINCES_FALLBACK

  const searchRecipients = useMutation({
    mutationFn: (q: string) => api.get<{ ok: boolean; items: PersonOption[] }>(
      `/ops/dispatch/recipients?q=${encodeURIComponent(q)}`),
    onSuccess: (res) => setRecipients(res.items),
  })

  const dispatch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.offline.post('/ops/dispatch/loads', body, 'Dispatched load'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'dispatch', 'loads'] })
      void qc.invalidateQueries({ queryKey: ['queue-counts'] })
      resetForm()
      setSent(true)
    },
  })

  function resetForm() {
    setShowForm(false)
    setRecipientQuery(''); setRecipients([]); setRecipient(null)
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = new FormData(e.currentTarget)
    const mineral = String(form.get('mineral_type') ?? '')
    const tonnage = Number(form.get('tonnage') ?? 0)
    const from = String(form.get('source_label') ?? '')
    const to = String(form.get('destination_label') ?? '')
    const truckId = String(form.get('truck_id') ?? '')
    const truckLabel = (trucksQuery.data?.items ?? [])
      .find((t) => t.id === truckId)?.plate ?? ''

    // Dispatching starts a real movement and enters the closure queue, so
    // confirm it rather than let a stray click send a truck.
    const ok = await confirm({
      title: 'Dispatch this load?',
      message: `${MINERALS.find((m) => m.value === mineral)?.label ?? mineral} · `
        + `${tonnage} t · ${from} → ${to}`
        + (truckLabel ? ` · ${truckLabel}` : ''),
      confirmLabel: 'Dispatch',
    })
    if (!ok) return

    dispatch.mutate({
      truck_id: String(form.get('truck_id') ?? ''),
      mineral_type: mineral,
      tonnage,
      source_label: from,
      destination_label: to,
      expected_recipient_uids: recipient ? [recipient.uid] : [],
      expected_recipient_names: recipient ? [recipient.name ?? recipient.email ?? ''] : [],
    })
  }

  const loads = data?.items ?? []

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Loads</h1>
          <p className="page-head__sub">
            Record a truck load and track it through to delivery.
          </p>
        </div>
        <Button
          className="fab-trigger__inline" icon={<PlusIcon />}
          onClick={() => { setSent(false); showForm ? resetForm() : setShowForm(true) }}
        >
          {showForm ? 'Cancel' : 'Record a load'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => { setSent(false); showForm ? resetForm() : setShowForm(true) }}
          aria-label={showForm ? 'Cancel' : 'Record a load'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      {sent && (
        <Alert tone="success">
          Load recorded. It now waits in the closure queue for sign-off.
        </Alert>
      )}

      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Record a load" />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                {dispatch.isError && (
                  <Alert tone="danger">
                    {dispatch.error instanceof ApiError
                      ? dispatch.error.message
                      : 'Could not record that load.'}
                  </Alert>
                )}

                {/* Four steps, at most three fields each, on every screen
                    size - a step is a decision, not a scroll position. */}
                <Wizard
                  steps={[
                    { title: 'Truck', content: (
                      <Select
                        label="Truck" name="truck_id" required defaultValue=""
                        disabled={trucksQuery.isLoading}
                      >
                        <option value="" disabled>
                          {trucksQuery.isLoading ? 'Loading trucks…' : 'Select a truck…'}
                        </option>
                        {(trucksQuery.data?.items ?? []).map((t) => (
                          <option key={t.id} value={t.id}>
                            {[t.plate, t.name, t.driver_name, t.org_name]
                              .filter(Boolean).join(' · ')}
                          </option>
                        ))}
                      </Select>
                    ) },
                    { title: 'Cargo', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <Select label="Material" name="mineral_type" required defaultValue="">
                          <option value="" disabled>Select the material…</option>
                          {MINERALS.map((m) => (
                            <option key={m.value} value={m.value}>{m.label}</option>
                          ))}
                        </Select>
                        <Input label="Tonnage" name="tonnage" type="number" step="any" min="0" required />
                      </div>
                    ) },
                    { title: 'Route', content: (
                      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
                        <Select label="Loading from" name="source_label" required defaultValue="">
                          <option value="" disabled>Select a site…</option>
                          {SITES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                        <Select label="Destination" name="destination_label" required defaultValue="">
                          <option value="" disabled>Select a province…</option>
                          {provinces.map((s) => <option key={s} value={s}>{s}</option>)}
                        </Select>
                      </div>
                    ) },
                    { title: 'Recipient', content: (
                      <div className="field">
                        <label className="field__label">Who is receiving it (optional)</label>
                        <div className="row" style={{ gap: 'var(--sp-2)' }}>
                          <input className="input" placeholder="Search by name or email"
                                 value={recipientQuery}
                                 onChange={(e) => setRecipientQuery(e.target.value)} />
                          <Button type="button" variant="secondary" loading={searchRecipients.isPending}
                                  onClick={() => recipientQuery.trim()
                                    && searchRecipients.mutate(recipientQuery.trim())}>
                            Search
                          </Button>
                        </div>
                        {recipient ? (
                          <span className="field__hint">
                            Sending to {recipient.name || recipient.email}
                            {' '}
                            <Button type="button" size="sm" variant="ghost"
                                    onClick={() => setRecipient(null)}>Change</Button>
                          </span>
                        ) : recipients.length > 0 && (
                          <div className="stack" style={{ gap: 2, marginTop: 'var(--sp-2)' }}>
                            {recipients.map((r) => (
                              <button key={r.uid} type="button" className="lrow"
                                      style={{ cursor: 'pointer', textAlign: 'left' }}
                                      onClick={() => { setRecipient(r); setRecipients([]) }}>
                                <div className="lrow__main">
                                  <div className="lrow__title">{r.name || r.email}</div>
                                  <div className="lrow__sub">{titleCase(r.role ?? '')}</div>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      {/* No `confirm` prop here: onSubmit already asks, and its question
                          names the material, tonnage, route and plate. Two dialogs
                          in a row would just train people to dismiss both. */}
                      <Button type="submit" loading={dispatch.isPending}>Record load</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {/* Capture vs reporting: the ledger of everyone's loads is reporting,
          so a capturer simply gets the button and a confirmation - no panel
          explaining what they cannot see. */}
      {canViewReports && (
        <Card>
          <CardHeader
            title="Loads"
            sub={loads.length ? `${loads.length} recorded` : undefined}
            action={(
              <div className="tab-scroll">
                {TABS.map((t) => (
                  <Button
                    key={t.value} size="sm"
                    variant={tab === t.value ? 'primary' : 'secondary'}
                    onClick={() => setTab(t.value)}
                  >
                    {t.label}
                  </Button>
                ))}
              </div>
            )}
          />
          <CardBody flush>
            {isError ? (
              <div style={{ padding: 'var(--sp-6)' }}>
                <Alert tone="danger">{(error as Error).message}</Alert>
              </div>
            ) : isLoading ? (
              <div style={{ padding: 'var(--sp-6)' }} className="stack">
                <Skeleton h={22} /><Skeleton h={22} w="76%" />
              </div>
            ) : loads.length === 0 ? (
              <EmptyState icon={<RouteIcon size={22} />} title="No loads yet"
                          body="Loads you record appear here with their status." />
            ) : (
              <>
                <div className="mobile-only">
                  {loads.map((l) => (
                    <Link key={l.id} to={`/dispatch/${l.id}`} className="lrow">
                      <div className="lrow__main">
                        <div className="lrow__title">{titleCase(l.mineral_type)}</div>
                        <div className="lrow__sub">
                          {formatNumber(l.tonnage, 1)} t · {l.source_label} → {l.destination_label}
                          {' · '}{formatDate(l.load_time || l.created_at)}
                        </div>
                      </div>
                      <div className="lrow__end"><StatusBadge status={l.status || 'dispatched'} /></div>
                    </Link>
                  ))}
                </div>
                <div className="table-wrap desktop-only">
                  <table className="table">
                    <thead>
                      <tr><th>Date</th><th>Material</th><th className="table__num">Tonnage</th>
                          <th>Route</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {loads.map((l) => (
                        <tr key={l.id}>
                          <td>{formatDate(l.load_time || l.created_at)}</td>
                          <td><Link to={`/dispatch/${l.id}`}>{titleCase(l.mineral_type)}</Link></td>
                          <td className="table__num">{formatNumber(l.tonnage, 1)} t</td>
                          <td>{l.source_label} → {l.destination_label}</td>
                          <td><StatusBadge status={l.status || 'dispatched'} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}
    </>
  )
}
